import React, { useContext, useEffect, useReducer } from 'react';
import {
	CustomerFullInfoContext,
	customerFullInfoInitialState,
	TCustomerFullInfoVisibleForm,
	TSelectedLocalizationItem,
	ISelectedStyleGroup,
} from './customerFullInfoContext';
import { customerFullInfoReducer } from './customerFullInfoReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import { CustomerFullInfoActionTypes } from './customerFullInfoActions';
import {
	CustomersContext,
	ICustomersContext,
} from 'modules/CustomersAndRestaurants/Customers/Customers/customersState/customersContext';
import {
	TLanguageIDs,
	IDataGetCustomerInfo,
	ICustomerLocalizationInfo,
	ICustomerStylesInfo,
	IGetCustomerInfo,
	IGetCustomerLocalizationInfo,
	IGetCustomerStylesInfo,
	ILatestSoftwareUpdateInfo,
	IGetBPAShort,
	IBpaShort,
} from 'types';
import { NON_EXISTENT_KEY } from 'appConstants';
import existingMediaSettingsKeys from 'public/mediaSettings.json';
import { useFetchData } from 'hooks/useFetchData';
import {
	getCustomerInfo,
	getCustomerLocalizationInfo,
	getCustomerStylesInfo,
	getCustomerBPAShort,
} from 'backend/customers';
// import { getLatestRestaurantSoftwareUpdatesData } from 'backend/restaurants';

const CustomerFullInfoState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(customerFullInfoReducer, customerFullInfoInitialState);
	const { showLoader, hideLoader, featureFlags } = useContext<IAppContext>(AppContext);
	const { fullInfoData } = useContext<ICustomersContext>(CustomersContext);

	const needLocalizationDataRequest =
		state.customerForFullInfo &&
		!state.localizationData &&
		state.customerFullInfoVisibleForm === 'localizationForm';

	const needStyleDataRequest =
		state.customerForFullInfo && !state.styleData && state.customerFullInfoVisibleForm === 'styleForm';

	const needSoftwareUpdatesRequest =
		state.customerForFullInfo &&
		!state.latestSoftwareUpdateData &&
		state.customerFullInfoVisibleForm === 'softwareForm';

	const needBpaDataRequest =
		state.customerForFullInfo && !state.bpaData && state.customerFullInfoVisibleForm === 'bpaForm';

	const {
		response: customerInfoResponse,
		refetch: customerInfoRefetch,
		loading: customerInfoLoading,
	} = useFetchData<IGetCustomerInfo, string>(fullInfoData.id ? getCustomerInfo : null, fullInfoData.id);

	const {
		response: localizationInfoResponse,
		refetch: localizationInfoRefetch,
		loading: localizationInfoLoading,
	} = useFetchData<IGetCustomerLocalizationInfo, string>(
		needLocalizationDataRequest ? getCustomerLocalizationInfo : null,
		state.customerForFullInfo?.id || '',
	);

	const {
		response: styleInfoResponse,
		refetch: styleInfoRefetch,
		loading: styleInfoLoading,
	} = useFetchData<IGetCustomerStylesInfo, string>(
		needStyleDataRequest ? getCustomerStylesInfo : null,
		state.customerForFullInfo?.id || '',
	);

	// const { response: latestSoftwareUpdatesDataResponse, refetch: latestSoftwareUpdatesDataRefetch } = useFetchData<
	// 	IGetLatestSoftwareUpdateInfo,
	// 	string
	// >(needSoftwareUpdatesRequest ? getLatestRestaurantSoftwareUpdatesData : null, state.customerForFullInfo?.id || ''); TODO need to add on BE

	const {
		response: bpaInfoResponse,
		refetch: bpaInfoRefetch,
		loading: bpaInfoLoading,
	} = useFetchData<IGetBPAShort, string>(
		needBpaDataRequest ? getCustomerBPAShort : null,
		state.customerForFullInfo?.id || '',
	);

	useEffect(() => {
		customerInfoLoading || localizationInfoLoading || styleInfoLoading || bpaInfoLoading
			? showLoader()
			: hideLoader();
	}, [customerInfoLoading, localizationInfoLoading, styleInfoLoading, bpaInfoLoading]);

	const changeCustomerFullInfoVisibleForm = (value: TCustomerFullInfoVisibleForm) => {
		dispatch({
			type: CustomerFullInfoActionTypes.CHANGE_CUSTOMER_FULL_INFO_VISIBLE_FORM,
			payload: {
				customerFullInfoVisibleForm: value,
			},
		});
	};

	const setCustomerForFullInfo = (value: IDataGetCustomerInfo | null) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_CUSTOMER_FOR_FULL_INFO,
			payload: {
				customerForFullInfo: value,
			},
		});
	};

	const setLocalizationGroup = (value: TSelectedLocalizationItem | null) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_LOCALIZATION_GROUP,
			payload: {
				selectedLocalizationGroup: value,
			},
		});
	};

	const setLocalizationLanguage = (value: TSelectedLocalizationItem | null) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_LOCALIZATION_LANGUAGE,
			payload: {
				selectedLocalizationLanguage: value,
			},
		});
	};

	const setLocalizationData = (value: ICustomerLocalizationInfo | null) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_LOCALIZATION_DATA,
			payload: {
				localizationData: value,
			},
		});
	};

	const setStyleData = (value: null | ICustomerStylesInfo) => {
		if (value) {
			const filteredDocumentSettings = value.documentSettings.filter((el) =>
				Object.keys(existingMediaSettingsKeys).includes(el.key || NON_EXISTENT_KEY),
			);
			dispatch({
				type: CustomerFullInfoActionTypes.SET_STYLE_DATA,
				payload: {
					styleData: { ...value, documentSettings: filteredDocumentSettings },
				},
			});
		} else {
			dispatch({
				type: CustomerFullInfoActionTypes.SET_STYLE_DATA,
				payload: {
					styleData: value,
				},
			});
		}
	};

	const setStyleGroup = (value: ISelectedStyleGroup) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_STYLE_GROUP,
			payload: {
				selectedStyleGroup: value,
			},
		});
	};

	const setStyleLanguage = (value: TLanguageIDs) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_STYLE_LANGUAGE,
			payload: {
				selectedStyleLanguage: value,
			},
		});
	};

	const setLatestSoftwareUpdateData = (value: ILatestSoftwareUpdateInfo | null) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_LATEST_SOFTWARE_UPDATE_DATA,
			payload: {
				latestSoftwareUpdateData: value,
			},
		});
	};

	const setBpaData = (value: Nullable<IBpaShort[]>) => {
		dispatch({
			type: CustomerFullInfoActionTypes.SET_BPA_DATA,
			payload: {
				bpaData: value,
			},
		});
	};

	useEffect(() => {
		customerInfoRefetch();

		return () => {
			setCustomerForFullInfo(null);
			changeCustomerFullInfoVisibleForm('mainForm');
			setLocalizationData(null);
			setStyleData(null);
			setLatestSoftwareUpdateData(null);
			setBpaData(null);
		};
	}, [fullInfoData.id]);

	useEffect(() => {
		customerInfoResponse && setCustomerForFullInfo(customerInfoResponse.data.customer);
	}, [customerInfoResponse]);

	useEffect(() => {
		localizationInfoRefetch();
	}, [state.customerForFullInfo, state.localizationData, state.customerFullInfoVisibleForm]);

	useEffect(() => {
		localizationInfoResponse && setLocalizationData(localizationInfoResponse.data);
	}, [localizationInfoResponse]);

	useEffect(() => {
		styleInfoRefetch();
	}, [state.customerForFullInfo, state.customerFullInfoVisibleForm, state.styleData]);

	useEffect(() => {
		if (styleInfoResponse) {
			const updatedMediaSettings = styleInfoResponse.data.mediaSettings.map((mediaSetting) => {
				const filteredMediaSettingGroups = mediaSetting.groups.filter((group) => {
					if (group.key === 'fiscal_receipt_dialog_background') {
						return featureFlags.HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND;
					}
					return true;
				});

				return { ...mediaSetting, groups: filteredMediaSettingGroups };
			});

			setStyleData({ ...styleInfoResponse.data, mediaSettings: updatedMediaSettings });
		}
	}, [styleInfoResponse]);

	// useEffect(() => {
	// 	latestSoftwareUpdatesDataResponse && setLatestSoftwareUpdateData(latestSoftwareUpdatesDataResponse.data);
	// }, [latestSoftwareUpdatesDataResponse]);
	// TODO need to add on BE
	// useEffect(() => {
	// 	latestSoftwareUpdatesDataRefetch();
	// }, [state.customerForFullInfo, state.customerFullInfoVisibleForm, state.latestSoftwareUpdateData]);

	useEffect(() => {
		bpaInfoRefetch();
	}, [state.customerForFullInfo, state.customerFullInfoVisibleForm, state.bpaData]);

	useEffect(() => {
		bpaInfoResponse && setBpaData(bpaInfoResponse.data.bpaSets);
	}, [bpaInfoResponse]);

	return (
		<CustomerFullInfoContext.Provider
			value={{
				changeCustomerFullInfoVisibleForm,
				setLocalizationGroup,
				setLocalizationLanguage,
				setLocalizationData,
				setStyleGroup,
				setStyleLanguage,
				setLatestSoftwareUpdateData,
				customerFullInfoVisibleForm: state.customerFullInfoVisibleForm,
				customerForFullInfo: state.customerForFullInfo,
				selectedLocalizationGroup: state.selectedLocalizationGroup,
				selectedLocalizationLanguage: state.selectedLocalizationLanguage,
				localizationData: state.localizationData,
				styleData: state.styleData,
				selectedStyleGroup: state.selectedStyleGroup,
				selectedStyleLanguage: state.selectedStyleLanguage,
				latestSoftwareUpdateData: state.latestSoftwareUpdateData,
				bpaData: state.bpaData,
			}}>
			{children}
		</CustomerFullInfoContext.Provider>
	);
};

export default CustomerFullInfoState;
